/** First-party popup + iframe protocol. No message can navigate or write vault data. */
export const VAULT_WINDOW_PATH = '/stock-vault/window';
const PROTOCOL = 'stock-vault-window-v1';
export interface VaultContext { symbol:string; openToken:number; dark:boolean; connected:boolean }
export type VaultCommand =
 | {type:'ready'} | {type:'hello'} | {type:'changed'} | {type:'focus-main'} | {type:'close'}
 | {type:'pick';symbol:string} | {type:'open-desk';symbol:string;name:string}
 | {type:'context';context:VaultContext};
export type VaultMessage = VaultCommand & {protocol:typeof PROTOCOL;channel:string};
const symbolOK=(v:unknown):v is string=>typeof v==='string'&&v.length>0&&v.length<=64&&/^[\w.\-:^=]+$/.test(v);
export function validChannel(v:unknown):v is string{return typeof v==='string'&&/^[a-zA-Z0-9_-]{16,80}$/.test(v);}
export function vaultMessage(channel:string,command:VaultCommand):VaultMessage{return {...command,protocol:PROTOCOL,channel};}
export function readVaultMessage(data:unknown,channel:string):VaultMessage|null {
 if(!data||typeof data!=='object')return null;
 const v=data as Record<string,unknown>;
 if(v.protocol!==PROTOCOL||v.channel!==channel||!validChannel(channel))return null;
 if(v.type==='ready'||v.type==='hello'||v.type==='changed'||v.type==='focus-main'||v.type==='close')return vaultMessage(channel,{type:v.type});
 if(v.type==='pick'&&symbolOK(v.symbol))return vaultMessage(channel,{type:'pick',symbol:v.symbol});
 if(v.type==='open-desk'&&symbolOK(v.symbol)&&typeof v.name==='string'&&v.name.length<=160)return vaultMessage(channel,{type:'open-desk',symbol:v.symbol,name:v.name});
 if(v.type==='context'&&v.context&&typeof v.context==='object'){
  const c=v.context as Record<string,unknown>;
  if((c.symbol===''||symbolOK(c.symbol))&&typeof c.dark==='boolean'&&typeof c.connected==='boolean'&&Number.isSafeInteger(c.openToken)&&Number(c.openToken)>=0)return vaultMessage(channel,{type:'context',context:{symbol:String(c.symbol),dark:c.dark,connected:c.connected,openToken:Number(c.openToken)}});
 }
 return null;
}
export function vaultWindowUrl(origin:string,base:string,channel:string,frame=false):string {
 const url=new URL(`${base.replace(/\/?$/,'/')}stock-vault/window`,origin);
 url.searchParams.set('channel',channel);if(frame)url.searchParams.set('frame','1');return url.href;
}
export function vaultSessionChannel(host:Window):string {
 try{const saved=host.sessionStorage.getItem('vault.detached.channel.v1');if(validChannel(saved))return saved;}catch{/* storage optional */}
 const channel=host.crypto.randomUUID().replace(/-/g,'');
 try{host.sessionStorage.setItem('vault.detached.channel.v1',channel);}catch{/* storage optional */}
 return channel;
}
interface Callbacks {
 context:()=>VaultContext; message:(message:VaultMessage)=>void;
 blocked:()=>void; closed:()=>void;
}
/** open() is called synchronously from the store subscription inside the user's click. */
export function createVaultPopupBridge(host:Window,base:string,channel:string,callbacks:Callbacks) {
 let popup:Window|null=null,ready=false,timer:number|undefined,lastSync=0;
 const origin=host.location.origin,url=vaultWindowUrl(origin,base,channel);
 const post=(command:VaultCommand)=>{if(popup&&!popup.closed){try{popup.postMessage(vaultMessage(channel,command),origin);}catch{/* closure/navigation checked on the next heartbeat */}}};
 const sync=()=>{if(ready){post({type:'context',context:callbacks.context()});lastSync=Date.now();}};
 const stop=()=>{if(timer!==undefined)host.clearInterval(timer);timer=undefined;};
 const receive=(event:MessageEvent)=>{
  if(!popup||event.origin!==origin||event.source!==popup)return;
  const message=readVaultMessage(event.data,channel);if(!message||message.type==='context'||message.type==='hello')return;
  if(message.type==='ready'){ready=true;sync();return;}
  if(message.type==='close'){popup.close();return;}
  if(message.type==='focus-main'){host.focus();return;}
  callbacks.message(message);
  if(message.type==='open-desk')host.focus();
 };
 host.addEventListener('message',receive);
 return {
  open(){
   if(popup&&!popup.closed){popup.focus();sync();return;}
   const width=Math.min(900,Math.max(640,Math.round(host.screen.availWidth*.45))),height=Math.max(480,Math.min(900,host.screen.availHeight-80));
   const features=`popup=yes,width=${width},height=${height},resizable=yes,scrollbars=yes`;
   // A stable per-tab name reuses an existing editor, including after a main-page refresh.
   popup=host.open('',`stock_vault_${channel}`,features);ready=false;
   if(!popup){callbacks.blocked();return;}
   try{if(popup.location.href!==url)popup.location.replace(url);}catch{popup.location.href=url;}
   popup.focus();stop();timer=host.setInterval(()=>{
    if(!popup||popup.closed){popup=null;ready=false;stop();callbacks.closed();return;}
    if(Date.now()-lastSync>=5000){if(!ready)post({type:'hello'});post({type:'context',context:callbacks.context()});lastSync=Date.now();}
   },500);
   post({type:'hello'});post({type:'context',context:callbacks.context()});
  },
  sync,
  changed(){if(ready)post({type:'changed'});},
  close(){popup?.close();popup=null;ready=false;stop();},
  // Do not close the independent editor during hot reload or a main-page navigation.
  dispose(){stop();host.removeEventListener('message',receive);},
 };
}
